<style scoped lang="scss">
  label {
    @apply text-gray-900 font-semibold text-12 mb-2;
  }

  input {
    @apply h-[41px] px-4 text-14 border bg-none text-gray-900 border-gray-300 rounded-[8px] focus:border-secondary-300 transition-colors;

    &::placeholder {
      @apply text-gray-900 text-opacity-50;
    }

    &::-webkit-calendar-picker-indicator {
      transition: fill 0.2s;
      background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="15" viewBox="0 0 24 24"><path fill="%23F0D7B9" opacity="0.5" d="M20 3h-1V1h-2v2H7V1H5v2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 18H4V8h16v13z"/></svg>');
    }

    &:focus {
      &::-webkit-calendar-picker-indicator {
        background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="15" viewBox="0 0 24 24"><path fill="%23E9E6E2" opacity="1" d="M20 3h-1V1h-2v2H7V1H5v2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 18H4V8h16v13z"/></svg>');
      }
    }
  }

  .disabled {
    input {
      @apply bg-gray-50;
    }
  }

  .hovering {
    input {
      @apply border-gray-500;
    }
    .prefix,
    .suffix {
      @apply border-gray-500;
    }
  }

  .is-focussed {
    input {
      @apply border-secondary-300;
    }
    .prefix,
    .suffix {
      @apply bg-secondary-300 border-secondary-300;
    }
  }

  .prefix,
  .suffix {
    @apply text-gray-900 text-14 transition-colors text-opacity-70;
  }

  .no-label {
    label {
      display: none;
    }
  }
</style>

<template>
  <ValidationProvider ref="provider" :vid="name" :name="validateAs" :rules="rules" :bails="false" v-slot="{ meta, errors, resetField }" tag="div" v-model="localValue">
    <div :class="[this.class, { 'disabled cursor-not-allowed pointer-events-none': disabled, 'is-focussed': isFocussed }]">
      <label v-if="label" :for="`id_${name}`" :class="[labelClass]" class="block mb-[4px]">
        {{ label }}
        <span v-if="(rules && typeof rules === 'string' && rules.includes('required')) || (typeof rules === 'object' && rules.required) || fakeRequired" class="text-red">*</span>
      </label>
      <div class="group relative z-0 flex items-center" :class="{ '': disabled, hovering: hovering }" @mouseover="hovering = true" @mouseleave="hovering = false">
        <span
          ref="prefix"
          v-if="$slots.prefix"
          class="prefix z-10 absolute border-t border-b border-l left-0 rounded-l-[4px] px-3 h-full flex items-center bg-gray-300"
          :class="{ '!bg-red !border-red !text-white': errors[0] && meta.validated && meta.touched }"
        >
          <slot name="prefix" v-bind:disabled="meta.disabled"></slot>
        </span>
        <input
          :value="format(localValue)"
          autocomplete="false"
          ref="input"
          type="text"
          inputmode="numeric"
          v-on="{ ...$listeners, input: (event: Event) => handleInput(event, resetField), focus: handleFocus, blur: handleBlur }"
          :class="[inputClass, 'w-full appearance-none focus:outline-none flex items-center ease-in-out', { '!border-red': errors[0] && meta.validated && meta.touched }]"
          :style="{ 'padding-right': suffixWidth, 'padding-left': prefixWidth }"
          :id="`id_${name}`"
          :placeholder="placeholder"
          :disabled="disabled"
          :aria-describedby="`description_${name}`"
        />
        <span
          ref="suffix"
          v-if="$slots.suffix"
          class="suffix z-10 absolute border-t border-b border-r right-0 rounded-r-[4px] px-3 h-full flex items-center bg-gray-300"
          :class="{ '!bg-red !border-red !text-white': errors[0] && meta.validated && meta.touched }"
        >
          <slot name="suffix" v-bind:disabled="meta.disabled"></slot>
        </span>
      </div>

      <span v-if="showError && errors[0] && meta.validated && meta.touched" class="text-red text-12 block mt-2">{{ errors[0] }}</span>
    </div>
  </ValidationProvider>
</template>

<script lang="ts">
  import { Field as ValidationProvider } from "vee-validate";
  import { Component, Vue, Prop, Emit, Watch, Ref } from "vue-facing-decorator";

  import { conformToMask } from "text-mask-core";
  import createNumberMask from "text-mask-addons/dist/createNumberMask";

  @Component({
    components: {
      ValidationProvider,
    },
  })
  export default class FormMoney extends Vue {
    @Prop() name?: string;

    @Prop() value?: string;

    @Prop() placeholder?: string;

    @Prop() rules?: string | object;

    @Prop() label?: string;

    @Prop({ default: true }) showError?: boolean;

    @Prop({ default: false }) disabled!: boolean;

    @Prop({ default: false }) autoFocus!: boolean;

    @Prop({ default: "text-12" }) labelClass!: string;

    @Prop({ default: "" }) inputClass!: string;

    @Prop({ default: false }) fakeRequired!: boolean;

    @Prop({ default: "" }) class?: string;

    @Ref() suffix!: HTMLSpanElement;

    @Ref() prefix!: HTMLSpanElement;

    @Ref() input!: HTMLInputElement;

    @Ref() readonly provider!: InstanceType<typeof ValidationProvider>;

    isFocussed = false;

    localValue: number | null = null;

    prefixWidth = "20px";

    suffixWidth = "20px";

    validAtleastOnce = false;

    hovering = false;

    currencyMask = createNumberMask({
      prefix: "",
      allowDecimal: true,
      includeThousandsSeparator: true,
      thousandsSeparatorSymbol: ".",
      decimalSymbol: ",",
      decimalLimit: 4,
      allowNegative: false,
    });

    private mounted() {
      this.setPreSuffixWidth();
      if (this.autoFocus) {
        this.input.focus();
      }
    }

    format(value: number | null) {
      if (Number.isNaN(value)) {
        return "";
      }
      return conformToMask(`${value}`.toString().replace(".", ","), this.currencyMask).conformedValue;
    }

    private updated() {
      this.setPreSuffixWidth();
    }

    private setPreSuffixWidth() {
      if (this.hasPrefix) {
        this.prefixWidth = `${this.prefix.offsetWidth + 16}px`;
      }
      if (this.hasSuffix) {
        this.suffixWidth = `${this.suffix.offsetWidth + 16}px`;
      }
    }

    private get validateAs(): string {
      if (this.label) {
        return this.label;
      }

      if (this.placeholder) {
        return this.placeholder;
      }

      if (this.name) {
        return this.name;
      }

      return `money field`;
    }

    private get hasPrefix(): boolean {
      return !!this.$slots.prefix;
    }

    private get hasSuffix(): boolean {
      return !!this.$slots.suffix;
    }

    @Emit("input")
    private handleInput(event: Event, resetField: (obj: { value: number | null }) => void) {
      if (event.target) {
        this.localValue = parseFloat((event.target as HTMLInputElement).value.replace(/\./g, "").replace(",", "."));
      }
      resetField({ value: this.localValue });
      return this.localValue;
    }

    @Emit("blur")
    private handleBlur(e: Event): Event {
      this.isFocussed = false;
      return e;
    }

    @Emit("focus")
    private handleFocus(e: Event): Event {
      this.isFocussed = true;
      return e;
    }

    @Watch("value", { immediate: true })
    private valueChanged(newValue: number | null) {
      this.localValue = newValue;
    }
  }
</script>
